/* eslint-disable max-len */
/**
 * Berth Availability Slot Timeline Controller
 *
 * Manages interactive timeline visualizations for berth availability and booking allocation.
 * Renders timeline views showing berth occupancy slots, handles user interactions for berth
 * selection, displays tooltips with boat/booking details, and provides sidebar functionality
 * for detailed berth information.
 *
 * @description Key Features:
 * - Interactive timeline with drag/zoom capabilities
 * - Berth allocation and availability visualization
 * - Tooltip displays with booking and boat dimension details
 * - Optional sidebar for extended berth information
 * - Toggleable docking status indicators (colored dots)
 * - Integration with booking occupancy workflows
 *
 * @see app/views/marinas/berth_availability_slot_timeline/index.html.erb Standalone timeline view
 *   @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component
 * @see app/views/marinas/marina_occupancy_timeline/index.html.erb Marina occupancy timeline view
 *   @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component
 * @see app/views/marinas/booking_occupancy/index.html.erb Booking allocation interface
 *   @see app/views/marinas/booking_occupancy/partials/index/_berth_allocation.html.erb Berth selection component
 *     @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component (via turbo frame)
 * @see app/views/marinas/booking_occupancy/new.html.erb New booking berth selection
 *   @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component
 * @see app/views/marina/requests/show.html.erb Request details with berth info
 *   @see app/views/shared/marina/_berth_information.html.erb Berth information component
 *     @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component (via turbo frame)
 * @see app/views/marina/bookings/show.html.erb Booking details with berth info
 *   @see app/views/shared/marina/_berth_information.html.erb Berth information component
 *     @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component (via turbo frame)
 *
 * @extends {Controller}
 */
/* eslint-enable max-len */
import { Controller } from "@hotwired/stimulus";
import { destroy } from "@rails/request.js";
import tippy from "tippy.js";
import { SidebarService } from "./shared/sidebar_service";

export default class extends Controller {
  static values = {
    items: Array,
    options: Object,
    groups: Array,
    autoResize: Boolean,
    start: String,
    end: String,
    enableSidebar: { type: Boolean, default: false },
    enableDockingStatus: { type: Boolean, default: false },
    contentTemplate: { type: String, default: "{{customerName}} │ {{boatName}} │ {{dateRange}}" },
    tooltipTemplate: { type: String, default: "{{boatDimensionsTitle}} | {{loaText}}: {{boatLength}}m | {{beamText}}: {{boatBeam}}m | {{draftText}}: {{boatDraft}}m" },
    noDimensionsText: String,
    unknownCustomerText: String,
    boatDimensionsText: String,
    loaText: String,
    beamText: String,
    draftText: String,
  };

  async connect() {
    try {
      this.setLoading(true);
      this.tooltipInstances = new Map(); // Track tooltip instances for cleanup

      const { DataSet, Timeline } = await import("vis-timeline/standalone");
      await import("vis-timeline/styles/vis-timeline-graph2d.css");

      const container = this.element;
      const items = new DataSet(this.processContent(this.itemsValue));
      const hasGroups = this.hasGroupsValue && this.groupsValue.length > 0;
      const processedGroups = hasGroups ? this.processContent(this.groupsValue) : null;

      const options = this.prepareOptions();
      if (this.autoResizeValue) {
        this.setupResizing(options);
      }

      const timelineArgs = [container, items, options];
      if (hasGroups) {
        const groups = new DataSet(processedGroups);
        timelineArgs.splice(2, 0, groups);
      }

      this.timeline = new Timeline(...timelineArgs);
      this.initTooltips();

      // Only add select event listener if sidebar is enabled
      if (this.enableSidebarValue) {
        this.timeline.on("select", (properties) => {
          if (properties.items && properties.items.length > 0) {
            const selectedItemId = properties.items[0];
            const selectedItem = this.itemsValue.find((item) => item.id === selectedItemId);

            if (selectedItem) {
              this.handleTimelineItemClick(selectedItem);
            }
          }
        });
      }

      this.setLoading(false);
    } catch (error) {
      console.error("Failed to initialize timeline:", error);
      this.setLoading(false);
      this.showError("Failed to load timeline visualization.");
    }
  }

  disconnect() {
    if (this.boundResizeHandler) {
      window.removeEventListener("resize", this.boundResizeHandler);
    }

    // Cleanup all tooltip instances
    this.tooltipInstances.forEach((tooltip) => {
      tooltip.destroy();
    });
    this.tooltipInstances.clear();

    if (this.timeline) {
      this.timeline.destroy();
      this.timeline = null;
    }
  }

  initTooltips() {
    // Lazy tooltip initialization - create tooltips only when items are hovered
    this.timeline.on("itemover", (properties) => {
      const itemId = properties.item;

      // Only create tooltip if it doesn't exist yet
      if (!this.tooltipInstances.has(itemId)) {
        const item = this.itemsValue.find((i) => i.id === itemId);
        if (item && item.data) {
          const tooltipContent = this.getTooltipContent(item.data);

          // Find the DOM element for this timeline item - access the actual DOM element
          const timelineItem = this.timeline.itemSet?.items?.[itemId];
          if (!timelineItem) {
            console.warn(`Timeline item ${itemId} not found in itemSet`);
            return;
          }
          const itemElement = timelineItem.dom?.box || timelineItem.dom;

          if (itemElement && itemElement.nodeType === Node.ELEMENT_NODE) {
            const tippyInstance = tippy(itemElement, {
              content: tooltipContent,
              touch: false,
              arrow: true,
              theme: "small-font",
            });

            this.tooltipInstances.set(itemId, tippyInstance);

            // Manually show the tooltip since hover already started
            tippyInstance.show();
          }
        }
      }
    });
  }

  getTooltipContent(data) {
    const {
      boat_dimensions: boatDimensions,
      berth_dimensions: berthDimensions,
      customer_name: customerName,
      boat_name: boatName,
      dates,
    } = data;

    if (!boatDimensions) {
      return this.noDimensionsTextValue;
    }

    const {
      length_in_cm: boatLengthInCm,
      beam_in_cm: boatBeamInCm,
      draft_in_cm: boatDraftInCm,
    } = boatDimensions;
    const {
      length_in_cm: berthLengthInCm,
      beam_in_cm: berthBeamInCm,
      draft_in_cm: berthDraftInCm,
    } = berthDimensions || {};
    const { start_date: startDate, end_date: endDate } = dates;
    const { cmToMeters, formatDate } = this.constructor;

    // Prepare template variables for tooltip
    const templateData = {
      customerName: customerName || this.unknownCustomerTextValue,
      boatName: boatName || "N/A",
      dateRange: `${formatDate(startDate)} - ${formatDate(endDate)}`,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      boatLength: cmToMeters(boatLengthInCm),
      boatBeam: cmToMeters(boatBeamInCm),
      boatDraft: cmToMeters(boatDraftInCm),
      berthLength: cmToMeters(berthLengthInCm),
      berthBeam: cmToMeters(berthBeamInCm),
      berthDraft: cmToMeters(berthDraftInCm),
      boatDimensionsTitle: this.boatDimensionsTextValue,
      loaText: this.loaTextValue,
      beamText: this.beamTextValue,
      draftText: this.draftTextValue,
      noDimensionsText: this.noDimensionsTextValue,
    };

    return this.constructor.applyTemplate(this.tooltipTemplateValue, templateData);
  }

  setupResizing(options) {
    this.headerElement = document.getElementById("berth-slots-timeline-header");

    if (!this.headerElement) {
      console.warn("Berth timeline header element not found, auto-resize may not work correctly");
    }

    this.calculateAndSetSize(options);

    // Debounce resize calculations for better performance
    this.boundResizeHandler = this.debounce(() => {
      if (this.timeline) {
        this.calculateAndSetSize();
        this.timeline.redraw();
      }
    }, 250);

    window.addEventListener("resize", this.boundResizeHandler);
  }

  calculateAndSetSize(optionsObj = null) {
    if (!this.headerElement) return;

    const viewportHeight = window.innerHeight;
    const headerHeight = this.headerElement.offsetHeight;
    // Min height with padding
    const timelineHeight = Math.max(300, viewportHeight - headerHeight - 20);

    if (optionsObj) {
      // eslint-disable-next-line no-param-reassign
      optionsObj.height = `${timelineHeight}px`;
    } else if (this.timeline) {
      this.timeline.setOptions({ height: `${timelineHeight}px` });
    }
  }

  processContent(dataArray) {
    return dataArray.map((data) => {
      const dataCopy = { ...data };
      if (dataCopy.data) {
        const { display_variant: displayVariant, docking_status: dockingStatus } = dataCopy.data;
        // Conditionally apply docking status class based on enableDockingStatus value
        const dockingClass = this.enableDockingStatusValue ? `docking-status-${dockingStatus}` : "";
        dataCopy.className = `timeline-item display-variant-${displayVariant} ${dockingClass}`.trim();
        const contentData = this.prepareContent(dataCopy.data);
        if (typeof contentData === "object") {
          dataCopy.content = contentData.text;
          dataCopy.tooltipContent = contentData.title; // Store for tippy tooltip
        } else {
          dataCopy.content = contentData; // Fallback for backward compatibility
        }
      } else if (typeof dataCopy.content === "string" && this.constructor.isHtmlString(dataCopy.content)) {
        const div = document.createElement("div");
        div.innerHTML = dataCopy.content.trim();
        dataCopy.content = div.firstChild;
      }
      return dataCopy;
    });
  }

  prepareContent(data) {
    const {
      customer_name: customerName,
      boat_name: boatName,
      boat_dimensions: boatDimensions,
      berth_dimensions: berthDimensions,
      dates,
    } = data;
    const {
      length_in_cm: boatLengthInCm,
      beam_in_cm: boatBeamInCm,
      draft_in_cm: boatDraftInCm,
    } = boatDimensions;
    const {
      length_in_cm: berthLengthInCm,
      beam_in_cm: berthBeamInCm,
      draft_in_cm: berthDraftInCm,
    } = berthDimensions || {};
    const { start_date: startDate, end_date: endDate } = dates;
    const { cmToMeters, formatDate } = this.constructor;

    // Prepare template variables
    const templateData = {
      customerName: customerName || this.unknownCustomerTextValue,
      boatName: boatName || "N/A",
      dateRange: `${formatDate(startDate)} - ${formatDate(endDate)}`,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      boatLength: cmToMeters(boatLengthInCm),
      boatBeam: cmToMeters(boatBeamInCm),
      boatDraft: cmToMeters(boatDraftInCm),
      berthLength: cmToMeters(berthLengthInCm),
      berthBeam: cmToMeters(berthBeamInCm),
      berthDraft: cmToMeters(berthDraftInCm),
      boatDimensionsTitle: this.boatDimensionsTextValue,
      loaText: this.loaTextValue,
      beamText: this.beamTextValue,
      draftText: this.draftTextValue,
      noDimensionsText: this.noDimensionsTextValue,
    };

    // Apply templates with placeholder replacement
    const displayText = this.constructor.applyTemplate(this.contentTemplateValue, templateData);
    const tooltipText = this.constructor.applyTemplate(this.tooltipTemplateValue, templateData);

    // Return object with both display text and tooltip data
    return {
      text: displayText,
      title: tooltipText, // This will show as tooltip in vis-timeline
    };
  }

  static applyTemplate(template, data) {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => (data[key] !== undefined ? data[key] : match));
  }

  static cmToMeters(cm) {
    return cm ? (cm / 100).toFixed(2) : "N/A";
  }

  static formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { day: "numeric", month: "long" });
  }

  static isHtmlString(str) {
    return /<\/?[a-z][\s\S]*>/i.test(str);
  }

  prepareOptions() {
    const options = { ...this.optionsValue };

    if (this.hasStartValue && this.startValue.trim() !== "") {
      options.start = new Date(this.startValue);
    }

    if (this.hasEndValue && this.endValue.trim() !== "") {
      options.end = new Date(this.endValue);
    }

    // Note: onSelect removed - using timeline.on('select') event listener instead

    if (options.editable?.remove) {
      options.onRemove = async (item, callback) => {
        try {
          this.setLoading(true);
          const response = await destroy(`/berth_availability_slots/${item.id}`);

          if (response.ok) {
            callback(item);
            this.setLoading(false);
          } else {
            callback(null);
            this.setLoading(false);
            this.showError("Failed to remove the slot. Server responded with an error.");
          }
        } catch (error) {
          console.error("Error handling slot removal:", error);
          callback(null);
          this.setLoading(false);
          this.showError("Network error when removing the slot.");
        }
      };
    }

    return options;
  }

  handleTimelineItemClick(item) {
    // Only execute if sidebar is enabled
    if (!this.enableSidebarValue) {
      return;
    }

    try {
      // Debug logging to verify item structure
      console.debug("Timeline item clicked:", item.id);

      // More robust date handling
      let date;
      if (item.start instanceof Date) {
        date = SidebarService.formatDate(item.start);
      } else if (typeof item.start === "string") {
        // Handle different date string formats
        [date] = item.start.split("T"); // Remove time part if present
      } else {
        console.error("Invalid date format in timeline item:", item.start);
        return;
      }

      // Verify we have a valid slot ID
      if (!item.id) {
        console.error("Timeline item missing ID:", item);
        return;
      }

      console.debug("Opening sidebar for slot:", item.id, "date:", date);

      // Open the sidebar with the slot information
      const success = SidebarService.openBerthSidebar({
        date,
        slotId: item.id,
        application: this.application, // Pass the application instance
        // berth_id will be deduced server-side from the slot
      });

      if (!success) {
        console.error("Failed to open sidebar for timeline item:", item);
      }
    } catch (error) {
      console.error("Error in handleTimelineItemClick:", error);
    }
  }

  showError(message) {
    const errorEvent = new CustomEvent("berth-timeline:error", {
      detail: { message },
      bubbles: true,
    });
    this.element.dispatchEvent(errorEvent);

    // Fallback to console error if no event listeners
    console.error(message);
  }

  setLoading(isLoading) {
    this.element.classList.toggle("is-loading", isLoading);

    const loadingEvent = new CustomEvent("berth-timeline:loading", {
      detail: { isLoading },
      bubbles: true,
    });
    this.element.dispatchEvent(loadingEvent);
  }

  // Debounce implementation to limit function calls
  // eslint-disable-next-line class-methods-use-this
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}
